import { path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { type LayerScopedPathParams, layerScopedPathSchema } from './shared'

export enum ShaderType {
  Data = 'data',
  Boundary = 'boundary'
}

const mappableDataSchema = z.object({
  name: z.string(),
  key: z.string(),
  units: z.string().optional(),
  shader: z.enum(ShaderType),
  negated: z.boolean().optional()
})

const rangeSchema = z.object({
  maximum: z.number(),
  minimum: z.number()
})

const literalDiscreteSchema = z.literal('discrete')
const literalContinuousSchema = z.literal('continuous')

const continuousStepSchema = z.object({
  color: z.string(),
  min: z.number().optional(),
  max: z.number().optional(),
  type: literalContinuousSchema
})

const discreteStepSchema = z.object({
  color: z.string(),
  label: z.string().optional(),
  value: z.number().optional(),
  type: literalDiscreteSchema,
  blue: z.number().min(0).max(255)
})

const gradientStop = z.object({
  color: z.string(),
  stop: z.number(),
  type: literalContinuousSchema
})

const gradientSchema = z.array(z.discriminatedUnion('type', [gradientStop, discreteStepSchema]))

const mixedGradientSchema = z.discriminatedUnion('type', [continuousStepSchema, discreteStepSchema])

const continuousDataSchema = mappableDataSchema.extend({
  discrete: z.literal(false),
  legend: z.object({
    points: z.number(),
    colors: z.array(mixedGradientSchema),
    range: rangeSchema,
    /**
     * Should the legend be displayed in descending order?
     * i.e., highest value at the top. This is unusual, but
     * Down Force does it since the blue no ground contact
     * is at the bottom and we want the lowest value to be
     * next to it.
     */
    descending: z.boolean().default(false)
  }),
  gain: z.number(),
  defined_range: rangeSchema,
  populated_range: rangeSchema,
  gradient: gradientSchema
})

const discreteDataSchema = mappableDataSchema.extend({
  discrete: z.literal(true),
  legend: z.object({
    colors: z.array(mixedGradientSchema)
  })
})

const legendConfigSchema = z.discriminatedUnion('discrete', [
  discreteDataSchema,
  continuousDataSchema
])

const pathFor = (p: LayerScopedPathParams & { lastRecordId?: number }) =>
  path(
    `${p.requestType}/streams/${p.orgCode}/${p.streamId}/layers/${p.applicationId}/${p.layerId}/definition`,
    { lastRecordId: p.lastRecordId }
  )

/**
 * The legend definition for a layer, includes metadata for rendering a legend.
 */
export const legendConfigApi = defineApi({
  pathSchema: layerScopedPathSchema,
  querySchema: z.object({
    lastRecordId: z.number().optional()
  }),
  pathFor,
  name: 'GetLegendDefinition',
  description:
    'Get the legend configuration for a layer including color gradients and value ranges',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema: legendConfigSchema
})

export type GradientStop = z.infer<typeof gradientStop>
export type MixedGradientArray = z.infer<typeof mixedGradientSchema>[]
export type DiscreteStop = Extract<
  z.infer<typeof mixedGradientSchema>,
  { type: z.infer<typeof literalDiscreteSchema> }
>
export type ContinuousStop = z.infer<typeof continuousStepSchema>

export const isDiscreteStop = (stop: DiscreteStop | ContinuousStop): stop is DiscreteStop =>
  stop.type === 'discrete'

export const isContinuousStop = (stop: DiscreteStop | ContinuousStop): stop is ContinuousStop =>
  stop.type === 'continuous'
